import { Link } from "react-router";
import { useTranslation } from "react-i18next";
import { Alert } from "./ui";
import { ApiError } from "../lib/api";
import { useOrgQuota } from "../lib/queries";
import type { QuotaResource } from "../lib/types";

/**
 * The quota where a person meets it: beside the New button on a list, and
 * in the refusal when a create crosses the ceiling.
 *
 * Both point at the Limits page with the resource in the query string, so
 * the page opens straight into the request for that resource rather than
 * asking the person to find the row they were just refused on.
 */

/** How full a ceiling is before it is worth a word. */
const NOTABLE_FROM = 0.8;

/**
 * "9 of 10 apps" next to the New button, and only once it is worth saying:
 * under 80% nothing shows, because a number on every list is a number nobody
 * reads. At the ceiling it names the way out.
 */
export function QuotaMeter({ orgId, resource }: { orgId: string | undefined; resource: QuotaResource }) {
  const { t } = useTranslation("limits");
  const quota = useOrgQuota(orgId);
  const line = quota.data?.resources[resource];
  if (!line || line.limit === 0 || line.used / line.limit < NOTABLE_FROM) return null;
  const full = line.used >= line.limit;
  return (
    <p className={`text-xs ${full ? "text-danger" : "text-warn"}`}>
      {t("meter.usage", { used: line.used, limit: line.limit, resource: t(`resources.${resource}`).toLowerCase() })}
      {" · "}
      <Link to={`/settings/limits?ask=${resource}`} className="underline underline-offset-2 hover:no-underline">
        {full ? t("meter.askFull") : t("meter.ask")}
      </Link>
    </p>
  );
}

/** What a `quota_exceeded` refusal carries. */
export interface QuotaRefusal {
  resource: QuotaResource;
  limit: number;
  used: number;
}

/** The refusal, if the error was one; null for anything else. */
export function quotaRefusal(error: unknown): QuotaRefusal | null {
  if (!(error instanceof ApiError) || error.code !== "quota_exceeded") return null;
  const details = (error.details ?? {}) as Partial<QuotaRefusal>;
  if (!details.resource || typeof details.limit !== "number" || typeof details.used !== "number") return null;
  return { resource: details.resource, limit: details.limit, used: details.used };
}

/**
 * The alert a refused create shows instead of the bare message: what the
 * ceiling is, and a way to ask for more that lands on the right form.
 */
export function QuotaAlert({ refusal }: { refusal: QuotaRefusal }) {
  const { t } = useTranslation("limits");
  return (
    <Alert tone="warning">
      <span className="block font-medium">
        {t("refused.title", { limit: refusal.limit, resource: t(`resources.${refusal.resource}`).toLowerCase() })}
      </span>
      <span className="mt-1 block">
        {t("refused.body")}{" "}
        <Link to={`/settings/limits?ask=${refusal.resource}`} className="font-medium underline underline-offset-2 hover:no-underline">
          {t("refused.ask")}
        </Link>
      </span>
    </Alert>
  );
}
